import { useNavigate, useSearchParams } from 'react-router-dom';
import { CarrierSearch, ImpersonatedCarrier } from '@types';
import { ActiveCarrier, JWT } from '@state';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import {
	IMPERSONATION_LOCAL_STORAGE_KEY,
	buildImpersonatedCarrier,
	canImpersonateFromJwt,
	fetchCarrierById,
	getIdOrExternalId,
} from './useImpersonation.helpers';

export const useImpersonateCarrier = () => {
	const [search, setSearch] = useSearchParams();
	const navigate = useNavigate();
	const jwt = useRecoilValue(JWT);
	const cid = search.get('cid');
	const storedCid = sessionStorage.getItem(IMPERSONATION_LOCAL_STORAGE_KEY.carrier);
	const setActiveCarrier = useSetRecoilState(ActiveCarrier);
	const carrierIdOrExternalId = getIdOrExternalId(cid);

	// Auto-resolve the active carrier from the ?cid param / sessionStorage (Router init path).
	const resolveCarrierFromRoute = async () => {
		if (!canImpersonateFromJwt(jwt)) return;
		if (carrierIdOrExternalId !== null) {
			if (storedCid !== null) {
				const activeCarrier: ImpersonatedCarrier = JSON.parse(storedCid);
				if (activeCarrier.id !== carrierIdOrExternalId && activeCarrier.customer_id !== carrierIdOrExternalId) {
					try {
						const carrier = await fetchCarrierById(carrierIdOrExternalId);
						sessionStorage.setItem(IMPERSONATION_LOCAL_STORAGE_KEY.carrier,
							JSON.stringify(carrier));
						setSearch((params) => {
							params.set('cid', `${carrier.id}`)
							return params
						});
						setActiveCarrier(carrier);
					} catch (error) {
						console.error('Failed to fetch carrier:', error);
						throw error;
					}
				} else {
					setSearch((params) => {
						params.set('cid', `${activeCarrier.id}`)
						return params
					});
					setActiveCarrier(activeCarrier);
				}
			} else {
				try {
					const fetchedCarrier = await fetchCarrierById(carrierIdOrExternalId);
					const carrier: ImpersonatedCarrier = {
						...fetchedCarrier,
						external_id: fetchedCarrier.customer_id
					};
					sessionStorage.setItem(IMPERSONATION_LOCAL_STORAGE_KEY.carrier,
						JSON.stringify(carrier));
					setSearch((params) => {
						params.set('cid', `${carrier.id}`)
						return params
					});
					setActiveCarrier(carrier);
				} catch (error) {
					console.error('Failed to fetch carrier:', error);
				}
			}
		} else if (storedCid !== null) {
			const activeCarrier: ImpersonatedCarrier = JSON.parse(storedCid);
			setSearch((params) => {
				params.set('cid', `${activeCarrier.id}`)
				return params
			});
			setActiveCarrier(activeCarrier);
		}
	}

	// Explicitly impersonate a carrier from a search result or an id.
	const impersonateCarrier = async (impersonation: CarrierSearch | number | string) => {
		if (!canImpersonateFromJwt(jwt)) return;
		if (typeof impersonation === 'number' || typeof impersonation === 'string') {
			const carrier = await fetchCarrierById(impersonation);
			setActiveCarrier(carrier);
			sessionStorage.setItem(IMPERSONATION_LOCAL_STORAGE_KEY.carrier, JSON.stringify(carrier));
			setSearch((params) => {
				params.set('cid', `${carrier.id}`)
				return params
			});
		} else {
			const carrier = buildImpersonatedCarrier(impersonation);
			setActiveCarrier(carrier);
			sessionStorage.setItem(IMPERSONATION_LOCAL_STORAGE_KEY.carrier, JSON.stringify(carrier));
			setSearch((params) => {
				params.set('cid', `${carrier.id}`)
				return params
			});
		}
	}

	// Clear the active carrier without navigating (used by the no-arg refrain()).
	const clearCarrier = () => {
		sessionStorage.removeItem(IMPERSONATION_LOCAL_STORAGE_KEY.carrier);
		setActiveCarrier(null);
	}

	// Stop impersonating the carrier and return to the carrier dashboard.
	const refrainCarrier = () => {
		clearCarrier();
		if (search.has('cid')) {
			search.delete('cid');
			setSearch(search);
		}
		navigate('/carrier/dashboard');
	}

	return {
		resolveCarrierFromRoute,
		impersonateCarrier,
		clearCarrier,
		refrainCarrier,
	}
}
